import { Ajax2 } from '@shared/Ajax';
import { LStorage } from '@shared/Storage';
import { Tree } from '@shared/ui-native/Tree';
import { Notify } from '@shared/ui-native/Notify';
import { getFilenameWithVersion } from '@shared/utils/utils';
import { CacheIDB } from '@shared/CacheIDB';

const fileTreeDataKey = 'file-tree-data'; // Cache will scope it to projectUuid+userUuid
const folderTreeDataKey = 'folder-tree-data'; // Cache will scope it to projectUuid+userUuid
const loadTimeKey = 'file-tree-load-time-' + kuura.getProjectId();
const treeCacheTimeSec = 24 * 60 * 60;

export interface IFileTreeFlattened {
    includesFiles: boolean;
    nodes: {
        id: number;
        uuid: string;
        text: string;
        depth: number;
        hidden: boolean;
        type: 'folder' | 'file';
    }[];
}

export const FileTreeService = {
    fetchFileTree: async (useCache = true): Promise<ITreeData> => {
        let cacheKeyServer = null;

        const fetchCacheKey = (): Promise<string> => {
            return Ajax2({
                url: kuura.getContextPath() + `/api/1/project/${kuura.getProjectUuid()}/file_tree_last_updated`,
            }).then((r: { lastUpdated: number }) => r.lastUpdated + '_permissions_todo');
        };

        const fetchRemote = async () => {
            const startTime = Date.now();
            try {
                const resp = await Ajax2({
                    url: kuura.getContextPath() + `/api/1/project/${kuura.getProjectId()}/file_tree.json`,
                    timeout: 90000,
                });

                // for each link find the model it links to and add it to link object,
                // we need it to filter links out of the tree based on the content of the model
                eachPostOrder(resp, 0, node => {
                    if (node.links?.length) {
                        node.links.forEach(link => {
                            link.model = findNodeByUuid(resp as any, link.modelUuid);
                        });
                    }
                });

                try {
                    if (!cacheKeyServer) {
                        cacheKeyServer = await fetchCacheKey();
                    }
                    resp.cacheKey = cacheKeyServer;
                    LStorage.setItem(loadTimeKey, Date.now() - startTime);
                    CacheIDB.set(fileTreeDataKey, resp, treeCacheTimeSec);
                } catch (e) {
                    console.warn('[ FileTreeService ] Failed to cache tree data', e);
                }

                return resp;
            } catch (error) {
                const isTimeoutError = error?.code === 'ECONNABORTED';
                if (isTimeoutError) {
                    Notify.error(kuura.getMessage('js_files.msg.notifications.error_loading_folder'));
                }
                throw error;
            }
        };

        //return new Promise<any>(resolve => { // fake slowdown for debug
        //    setTimeout(() => resolve(Ajax({ url: `/api/1/project/${kuura.getProjectId()}/file_tree.json` })), 3000 + 500);
        //}) as any

        // if (useCache && FileTreeService.isSlowLoadTime()) {
        // Set to false to disable cache for debugging
        if (true) {
            return new Promise(async (done, fail) => {
                const cachedData = await CacheIDB.get(fileTreeDataKey);
                if (!cachedData || !Array.isArray(cachedData?.folders)) {
                    console.log('[ FileTreeService ] File tree data cache miss');
                    return fetchRemote().then(done);
                }

                cacheKeyServer = await fetchCacheKey();
                console.log('[ FileTreeService ] cacheKeyServer', cacheKeyServer);
                console.log('[ FileTreeService ] cachedData.cacheKey', cachedData.cacheKey);

                if (cacheKeyServer !== cachedData.cacheKey) {
                    console.log('[ FileTreeService ] File tree data cache stale');
                    return fetchRemote().then(done);
                }

                console.log('[ FileTreeService ] File tree data cache hit');
                return done(cachedData);
            });
        } else {
            return fetchRemote();
        }
    },

    fetchFolderTree: (useCache = false): Promise<ITreeData> => {
        const fetchRemote = () => {
            return Ajax2({
                url: kuura.getContextPath() + `/api/1/project/${kuura.getProjectId()}/folder_tree.json`,
                timeout: 60000,
            }).then(resp => {
                try {
                    CacheIDB.set(folderTreeDataKey, resp, treeCacheTimeSec);
                } catch (e) {
                    console.warn('[ FileTreeService ] Failed to cache folder tree data', e);
                }
                return resp;
            });
        };

        if (useCache && FileTreeService.isSlowLoadTime()) {
            return new Promise(async (done, fail) => {
                const longCache = await CacheIDB.get(folderTreeDataKey);
                if (longCache && Array.isArray(longCache?.children)) {
                    console.log('[ FileTreeService ] Returning cached folder data');
                    return done(longCache);
                } else {
                    console.log('[ FileTreeService ] Folder tree data cache miss');
                    return fetchRemote().then(done);
                }
            });
        } else {
            console.log('[ FileTreeService ] Fetching fresh folder tree data');
            return fetchRemote();
        }
    },

    getFolderPath: (treeData: ITreeData, initialFolderId: number | string): string[] => {
        const folderIndex = {};
        FileTreeService.eachPostOrder(treeData, 0, folder => {
            folderIndex[folder.id] = folder;
        });
        return FileTreeService.getFolderPathFromIndex(folderIndex, initialFolderId);
    },

    getFolderPathFromIndex: (foldersIndex: Record<string, ITreeData>, initialFolderId: number | string): string[] => {
        let path: string[] = [];
        let currParent = foldersIndex[initialFolderId];
        while (currParent) {
            path.unshift(currParent.name);
            currParent = foldersIndex[currParent.parentFolderId];
        }
        if (path.length >= 2) {
            // remove root when its not the only folder, to save space
            path = path.slice(1);
        }
        return path;
    },

    mergeTreeStates: (srcTree: Tree, destTree: Tree) => {
        if (!srcTree || !destTree) return;

        srcTree.each(srcNode => {
            const destNode = destTree.getByHeadUuid(srcNode.headUuid);
            if (destNode && destNode.isFileOrLink()) {
                destNode.state.checked = srcNode.state.checked;
                destNode.state.expanded = srcNode.state.expanded;
            }
        });
        destTree.view?.render();
        destTree.calculateCheckboxes();
    },

    getLastLoadTime: () => parseInt(LStorage.getItem(loadTimeKey), 10) || 0,
    isSlowLoadTime: () => FileTreeService.getLastLoadTime() > 5000,
    clearCache: async () => {
        try {
            await CacheIDB.remove(fileTreeDataKey);
            await CacheIDB.remove(folderTreeDataKey);
        } catch (e) {
            console.log('[ FileTreeService ] Cache clear error', e);
        }
    },
    findNodeByUuid,
    findNodeById,
    flattenFiletree,
    eachPostOrder,
};

function findNodeById(treeData: ITreeData, id: number | string, onlyFolders = false, onlyFiles = false) {
    let found = null;
    eachPostOrder(treeData, 0, node => {
        if (!found) {
            if (node.id == id) {
                found = node;
            } else if (node.models?.length) {
                node.models.find(c => {
                    if (onlyFolders) {
                        if (c.hasOwnProperty('tolerance') && c.id == id) {
                            found = c;
                        }
                    } else if (onlyFiles) {
                        if (!c.hasOwnProperty('tolerance') && c.id == id) {
                            found = c;
                        }
                    } else {
                        if (c.id == id) {
                            found = c;
                        }
                    }
                });
            }
        }
    });
    return found;
}

function findNodeByUuid(treeData: ITreeData, uuid: string) {
    if (!uuid) return null;

    let found = null;
    eachPostOrder(treeData, 0, node => {
        if (!found) {
            if (node.uuid == uuid) {
                found = node;
            } else if (node.models?.length) {
                node.models.find(c => {
                    if (c.uuid == uuid) {
                        found = c;
                        found.parentUuid = node.uuid;
                    }
                });
            }
        }
    });
    return found;
}

function eachPostOrder(node, depth, callback: (node) => void) {
    if (!node) return;
    for (const child of node.folders ?? node.children ?? []) {
        if (child) {
            eachPostOrder(child, depth, callback);
        }
    }
    callback(node);
}

function flattenFiletree(treeData: ITreeData, includeFiles = false, includeRoot = false, hideEmptyFolders = false, filter: (item) => boolean): IFileTreeFlattened {
    if (!treeData) {
        console.warn('No file tree data');
        return {
            includesFiles: includeFiles,
            nodes: [],
        };
    }
    treeData = JSON.parse(JSON.stringify(treeData));
    const out = [];

    // hide empty folders
    if (hideEmptyFolders) {
        eachPostOrder(treeData, 0, node => {
            node.hidden = false; // file

            const visibleFolderChildren = (node.folders || []).filter(n => !n.hidden);
            const visibleModelsChildren = (node.models || []).filter(filter);

            if (visibleModelsChildren.length + visibleFolderChildren.length === 0) {
                node.hidden = true;
            }
        });
    }

    // flatten
    function visit(node, depth) {
        if (node.hidden) return;

        const nodeOut = {
            id: node.id,
            uuid: node.uuid,
            text: getFilenameWithVersion(node),
            type: node.layerCounts ? 'file' : 'folder',
            childrenCount: 0,
            depth,
        };

        out.push(nodeOut);

        // visit children
        if (node.layerCounts) return; // files don't have children

        let children = [];
        if (node.folders) {
            children = children.concat(node.folders);
        }
        if (includeFiles && node.models) {
            children = children.concat(node.models);
        }

        if (children.length === 0) return;

        depth += 1;
        for (const child of children) {
            if (typeof filter === 'function' && !filter(child)) continue;
            visit(child, depth);
        }
    }

    visit(treeData, !!includeRoot ? 0 : -1);

    return {
        includesFiles: includeFiles,
        nodes: !!includeRoot ? out : out.splice(1),
    };
}
